import { useMemo } from "react";
import { Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEstimatedProgress } from "@/hooks/useEstimatedProgress";

const SHIMMER_DURATION_MS = 2500;
const GLOW_DURATION_MS = 3500;

/** Negative delay that aligns an infinite animation to a global clock. */
function syncDelay(durationMs: number): number {
  const elapsed = typeof performance !== "undefined" ? performance.now() : 0;
  return -(elapsed % durationMs);
}

interface PendingProgressCardProps {
  createdAt: string;
  mediaType: "image" | "video";
  isComplete?: boolean;
}

export function PendingProgressCard({
  createdAt,
  mediaType,
  isComplete,
}: PendingProgressCardProps) {
  const { t } = useLanguage();
  const estimated = useEstimatedProgress({
    startTime: createdAt,
    mediaType,
  });
  const syncDelays = useMemo(() => ({
    shimmer: syncDelay(SHIMMER_DURATION_MS),
    glow: syncDelay(GLOW_DURATION_MS),
  }), []);

  const progress = isComplete ? 100 : estimated.progress;
  const label = isComplete ? t.generate.progressDone : estimated.label;

  return (
    <div className="relative border-[2px] border-border aspect-[9/16] flex flex-col items-center justify-center gap-2 md:gap-3 overflow-hidden bg-secondary/30">
      {/* Breathing radial glow */}
      {!isComplete && (
        <div
          className="absolute inset-0 animate-shimmer-glow"
          style={{ animationDelay: `${syncDelays.glow}ms` }}
        />
      )}

      {/* Diagonal shimmer sweep */}
      {!isComplete && (
        <div
          className="absolute inset-0 animate-shimmer"
          style={{ animationDelay: `${syncDelays.shimmer}ms` }}
        />
      )}

      {/* Skeleton content shapes */}
      {!isComplete && (
        <div className="absolute inset-x-0 top-[12%] flex flex-col items-center gap-2.5 px-5">
          <div className="w-4/5 aspect-[4/5] rounded-xl bg-muted-foreground/10" />
          <div className="w-3/5 h-2 rounded-full bg-muted-foreground/10" />
          <div className="w-2/5 h-2 rounded-full bg-muted-foreground/10" />
        </div>
      )}

      {/* Completion checkmark */}
      {isComplete && (
        <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <Check className="w-5 h-5 md:w-7 md:h-7 text-primary" />
        </div>
      )}

      {/* Progress info */}
      <div className="relative flex flex-col items-center gap-1.5 w-full px-4">
        {/* Progress bar with soft glow */}
        <div className="w-full max-w-[120px] h-1.5 bg-border/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${progress}%`,
              boxShadow: "0 0 8px hsl(var(--brand) / 0.4)",
            }}
          />
        </div>

        {/* Percentage */}
        <span className="text-muted-foreground text-[10px] md:text-xs font-medium tabular-nums">
          {progress}%
        </span>

        {/* Step label */}
        <span className="text-muted-foreground text-xs md:text-sm font-medium">
          {label}
        </span>
      </div>
    </div>
  );
}
